import React, {useEffect, useState, useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import EnhancedTableHead from './EnhancedTableHead';
import {capitalize} from '../../../../../utils/helpers';

interface Data {
    variable: string;
    value: string[];
}

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
    /**
     * The form data coming from the parent has extra fields, but we only care
     * about group_options
     */
    formData: {group_options: {name: string; value?: string}[]};
    setFormData: any;
    /** Set whether the form is valid and can proceed to the next step */
    handleNext: any;
    /**
     * The groups and options derived from the given samples. For example
     * ```typescript
     * {Condition: ['Hypoxia', 'Normal'], Diet: ['Reduced', 'Regular']}
     * ```
     * can be gotten from the Jaslyn dataset.
     * */
    groupLevels: {[s: string]: string[]};
    error: any;
    setError: any;
}

export const EnhancedTable = ({formData, setFormData, handleNext, groupLevels, error, setError}: EnhancedTableProps) => {
    const {group_options: selectedGroupOption} = formData;
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof Data>('variable');

    const rows: Data[] = useMemo(() => {
        return Object.entries(groupLevels)
            .map(([key, value]) => {
                return {
                    variable: key.split('_').map(capitalize).join(' '),
                    value: value
                };
            })
            .sort((a, b) => a.variable.localeCompare(b.variable));
    }, [groupLevels]);

    useEffect(() => {
        selectedGroupOption.length < 1 || selectedGroupOption.length > 2 || !selectedGroupOption.every((o: any) => o.value)
            ? setError(`Please select between 1 and 2 options and select a control for each before continuing.`)
            : setError(null);
    }, [selectedGroupOption]);

    useEffect(() => {
        error ? handleNext(false) : handleNext(true);
    }, [error]);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = rows.map((n: Data) => ({name: n.variable}));
            setFormData((prev: any) => {
                return {
                    ...prev,
                    group_options: newSelected
                };
            });
            return;
        }
        setFormData((prev: any) => {
            return {
                ...prev,
                group_options: []
            };
        });
    };

    /** Handle clicking the select checkbox for a group option */
    const handleClick = (name: string) => {
        const selectedIndex = selectedGroupOption?.findIndex((item: any) => item.name === name);
        let newSelected: {name: string}[] = [];

        if (selectedIndex === -1) {
            newSelected = [...selectedGroupOption, {name}];
        } else {
            newSelected = [...selectedGroupOption.slice(0, selectedIndex), ...selectedGroupOption.slice(selectedIndex + 1)];
        }
        setFormData((prev: any) => {
            return {
                ...prev,
                group_options: newSelected
            };
        });
    };

    /** Handle clicking a radio button for the value of a group option (setting the control) */
    const handleControlClick = (name: string, value: string) => {
        setFormData((prev: any) => {
            return {
                ...prev,
                group_options: [
                    ...selectedGroupOption?.map((item: any) => {
                        return item.name === name ? {name, value: value} : item;
                    })
                ]
            };
        });
    };

    /** Check whether a group option is selected */
    const isSelected = (name: string) => selectedGroupOption?.map((e: any) => e.name).indexOf(name) !== -1;

    return (
        <Box p={4} mr={5} sx={{borderRadius: '12px', bgcolor: 'surface-container-lowest'}}>
            <Box sx={{width: '100%', mb: 2}}>
                <TableContainer sx={{border: '1px solid', borderColor: 'outline', borderRadius: '6px'}}>
                    <Table aria-labelledby="tableTitle" size="medium">
                        <EnhancedTableHead
                            numSelected={selectedGroupOption?.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows?.length}
                        />
                        <TableBody>
                            {rows?.map((row: Data, index: any) => {
                                const isItemSelected = isSelected(row.variable);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.variable}
                                        selected={isItemSelected}
                                        sx={{cursor: 'pointer', borderTop: '1px solid rgba(224, 224, 224, 1)'}}>
                                        <TableCell width={'15%'} align="left">
                                            <Checkbox
                                                sx={{color: 'outline'}}
                                                checked={isItemSelected}
                                                onClick={_event => handleClick(row.variable)}
                                                inputProps={{
                                                    'aria-labelledby': labelId
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell component="th" id={labelId} scope="row" padding="none" align="left" width={'30%'}>
                                            <Typography variant="body" size="medium" pl={2}>
                                                {row.variable}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="left" width={'30%'}>
                                            {row?.value?.map((value: string) => (
                                                <Typography variant="body" size="medium" sx={{padding: '13px 0'}}>
                                                    {value}
                                                </Typography>
                                            ))}
                                        </TableCell>
                                        <TableCell align="left">
                                            {isItemSelected && (
                                                <>
                                                    <FormControl>
                                                        <RadioGroup
                                                            aria-labelledby="demo-radio-buttons-group-label"
                                                            defaultValue="optionOne"
                                                            name="radio-buttons-group"
                                                            onChange={e => handleControlClick(row.variable, e.target.value)}>
                                                            {row.value.map((val: string) => (
                                                                <FormControlLabel
                                                                    value={val}
                                                                    control={<Radio size="small" />}
                                                                    checked={
                                                                        selectedGroupOption?.find((o: any) => o.name === row.variable)
                                                                            ?.value === val
                                                                    }
                                                                    label={
                                                                        <Typography variant="body" size="medium">
                                                                            Set as control
                                                                        </Typography>
                                                                    }
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    </FormControl>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};
